import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { WizardStage, WizardProps, SubmitState, StageField, FieldComponentProps, OnProgressEventHandler } from './types';
import { Form, FormikConfig, FormikHelpers, FormikValues, useFormikContext } from 'formik';
import * as Yup from 'yup';
import style from './style.module.scss';
import { FormElementsGroup, Button, ButtonRowContainer, useDismissableErrorMessage } from '../../ui';
import ValidationFeedbackFormik from '../ValidationFeedbackFormik';
import { SubmitProgressBox } from '@n2-common/ui/SubmitProgress';
import { arraysEqual } from '@n2-common/lib/utils';
import { useFormDataQuery } from '@n2-common/lib/useFormDataQuery';
import { fromRawSchemas } from './schema-trees';
import useTracking from '@n2-common/lib/useTracking';
import { joinNames } from '@n2-common/ui/helpers';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

export function ScrollToFieldError() {
    const { isValid, errors, isSubmitting, setFieldTouched } = useFormikContext();
    useEffect(() => {
        if (isValid || !isSubmitting) {
            return;
        }
        const keys = Object.keys(errors);
        if (keys.length === 0) {
            return;
        }
        const firstField = keys[0];
        let element: HTMLElement | null = document.getElementsByName(firstField)?.[0] || document.getElementById(firstField);
        if (!element) {
            const subField = errors[firstField as keyof typeof errors];
            const subKeys = Object.keys(subField);
            if (subKeys.length === 0) {
                return;
            }
            const fullName = `${firstField}.${subKeys[0]}`;
            element = document.getElementById(fullName);
            if (!element) {
                console.log('Found no field');
                return;
            }
            setFieldTouched(fullName, true);
        } else {
            setFieldTouched(firstField, true);
        }
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus({ preventScroll: true });
    }, [isSubmitting, errors, isValid, setFieldTouched]);
    return null;
}

function ScrollToNext({ wizardStep, initialStep }: { wizardStep: number; initialStep: number }) {
    useEffect(() => {
        const step = initialStep !== 0 && wizardStep === initialStep ? 0 : wizardStep;
        const element = document.getElementById(getStageId(step));
        if (element) {
            const nextElement = element.querySelector('input, select');
            if (nextElement && 'focus' in nextElement && typeof nextElement.focus == 'function') {
                nextElement.focus({ preventScroll: true });
            }
            element.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        }
    }, [wizardStep, initialStep]);
    return null;
}

const Wizard = <T extends FormikValues = FormikValues>({
    stages,
    initialValues,
    onSubmit,
    formName,
    getSubmitButtonText,
    majorNumberUsedForTracking,
    onSsnStepFinished,
    ssnStep,
    initialStep = 0,
}: WizardProps<T>): JSX.Element => {
    const { t } = useTranslation('common');
    const saveProgress = localStorage.getItem('save-progress') == '1';
    const { ErrorOutput, setErrorMessage } = useDismissableErrorMessage(1_000_000_000);
    const [progressingText, setProgressingText] = useState<string>();
    const [currentStep, setCurrentStep] = useState<number>(initialStep);
    const [submitState, setSubmitState] = useState<SubmitState>('NotSent');
    const [hiddenStages, setHiddenStages] = useState(getHiddenStages(stages, initialValues));

    const initialValuesRef = useRef(initialValues);
    useTracking(currentStep, stages, majorNumberUsedForTracking);

    const next = getNextStep(stages.length, hiddenStages, currentStep);

    if (saveProgress) {
        const saved = localStorage.getItem(formName);
        if (saved) {
            try {
                initialValuesRef.current = { ...initialValuesRef.current, ...JSON.parse(saved) } as T;
            } catch (e) {
                console.log(e);
            }
        }
    }

    const rawSchemas = useMemo(() => getRawSchemas(stages), [stages]);

    const config = useMemo<FormikConfig<T>>(() => {
        const activeRawSchemas = rawSchemas.filter((s, i) => i <= currentStep && !hiddenStages.includes(i));
        const schema = fromRawSchemas(activeRawSchemas);
        const onProgress: OnProgressEventHandler = (event) => {
            if (typeof event == 'string') {
                setSubmitState(event as SubmitState);
            } else {
                const { type } = event;
                setSubmitState(type);
                switch (type) {
                    case 'Error':
                        setErrorMessage(event.options);
                        break;
                    case 'Processing':
                        setProgressingText(event.message || t<string>('FormSubmitButton.Processing'));
                        break;
                    default:
                        break;
                }
            }
        };
        return {
            initialValues: initialValuesRef.current,
            validationSchema: schema,
            validate: (values) => {
                const stagesThatShouldBeHidden = getHiddenStages<T>(stages, values);
                if (!arraysEqual(hiddenStages, stagesThatShouldBeHidden)) {
                    setHiddenStages(stagesThatShouldBeHidden);
                }
                saveProgress && localStorage.setItem(formName, JSON.stringify(values));
            },
            onSubmit: async (values, helpers) => {
                const next = getNextStep(stages.length, hiddenStages, currentStep);
                if (currentStep === ssnStep && onSsnStepFinished) {
                    onSsnStepFinished(values).catch((ex) => console.error(ex));
                }
                if (next != null) {
                    setFieldsNotTouched(rawSchemas, next, helpers);
                    setCurrentStep(next);
                } else {
                    await onSubmit(values, onProgress);
                }
            },
        };
    }, [rawSchemas, currentStep, hiddenStages, setErrorMessage, t, stages, saveProgress, formName, ssnStep, onSsnStepFinished, onSubmit]);

    return (
        <ValidationFeedbackFormik {...config}>
            <Form data-nameforuitesting="multistagewizard" data-matomo-name={formName} name={formName}>
                {stages
                    .filter((_, i) => i <= currentStep)
                    .map((stage, i) => (
                        <Stage
                            key={`stage-${i}`}
                            currentStep={currentStep}
                            formName={formName}
                            initialValuesRef={initialValuesRef}
                            stage={stage}
                            step={i}
                        />
                    ))}
                <ButtonRowContainer>
                    <SubmitProgressBox
                        state={submitState}
                        submitText={t<string>('FormSubmitButton.Submitted')}
                        processingText={progressingText || t<string>('FormSubmitButton.Processing')}
                    ></SubmitProgressBox>
                    <Button
                        className={style.submitButton}
                        type="submit"
                        uiType="primary"
                        uiTestingId="wizardPrimary"
                        disabled={submitState === 'Submitted' || submitState === 'Processing'}
                    >
                        {getSubmitButtonText
                            ? getSubmitButtonText(submitState, next != null)
                            : getDefaultSubmitButtonText(submitState, next != null, t)}
                    </Button>
                </ButtonRowContainer>
                <ScrollToNext wizardStep={currentStep} initialStep={initialStep} />
                <ScrollToFieldError />
                <ErrorOutput />
            </Form>
        </ValidationFeedbackFormik>
    );
};

// Exported for access in unittest.
export function getRawSchemas<T extends Record<string, unknown>>(stages: WizardStage<T>[]) {
    return stages.map((group) =>
        group.fields.reduce((mem, field) => ({ ...mem, [field.name]: field.validation }), {} as Record<string, Yup.AnySchema>)
    );
}

type StageProps<T extends FormikValues> = {
    step: number;
    currentStep: number;
    formName: string;
    initialValuesRef: React.MutableRefObject<T>;
    stage: WizardStage<T>;
};

function Stage<T extends Record<string, unknown>>({ currentStep, formName, initialValuesRef, stage, step }: StageProps<T>) {
    const query = useCallback(
        (values: T) => ({
            legend: typeof stage.legend == 'function' ? stage.legend(values) : stage.legend,
            visible: currentStep >= step && (stage.hidden == undefined || !stage.hidden(values)),
        }),
        [stage, step, currentStep]
    );
    const { legend, visible } = useFormDataQuery(formName, query, initialValuesRef.current);

    const fields = useMemo(() => {
        return stage.fields.map((field) => {
            return (
                <FieldComponentWrapper
                    key={field.name}
                    name={field.name}
                    hidden={field.hidden}
                    initialValuesRef={initialValuesRef}
                    Component={field.Component}
                    componentProps={field.componentProps}
                    formName={formName}
                />
            );
        });
    }, [stage.fields, initialValuesRef, formName]);

    return (
        <FormElementsGroup legend={legend} className={visible ? undefined : style.hidden} id={getStageId(step)}>
            {fields}
            {stage.afterStageElement ?? null}
        </FormElementsGroup>
    );
}

type FieldComponentWrapperProps<T extends FormikValues> = {
    name: string;
    hidden: StageField<T>['hidden'];
    initialValuesRef: React.MutableRefObject<T>;
    Component: StageField<T>['Component'];
    componentProps: StageField<T>['componentProps'];
    formName: string;
};

function FieldComponentWrapper<T extends FormikValues>({
    Component,
    componentProps,
    hidden,
    initialValuesRef,
    formName,
    name,
}: FieldComponentWrapperProps<T>) {
    //console.log(`Render ${name}`);
    const queryComponentProps = useMemo<(values: T) => FieldComponentProps & { hidden: boolean }>(
        () => (values: T) => ({
            hidden: typeof hidden == 'function' ? hidden(values) : hidden === true,
            ...(typeof componentProps == 'function' ? componentProps(values) : componentProps),
        }),
        [hidden, componentProps]
    );

    const { hidden: isHidden, ...props } = useFormDataQuery(formName, queryComponentProps, initialValuesRef.current);
    return (
        <div className={joinNames(style.field, isHidden ? style.hidden : undefined)}>
            <Component name={name} id={name} {...props} />
        </div>
    );
}

export function addPleaseSelectOption(existingOptions: { text: string; value: string }[], t: TFunction): { value: string; text: string }[] {
    return [{ text: t<string>('SelectInput.PleaseSelect'), value: '' }, ...existingOptions];
}

function setFieldsNotTouched<T extends FormikValues>(rawSchemas: Record<string, Yup.AnySchema>[], next: number, helpers: FormikHelpers<T>) {
    const nextSchema = rawSchemas.find((s, i) => i == next);
    nextSchema && Object.keys(nextSchema).forEach((key) => helpers.setFieldTouched(key, false));
}

function getHiddenStages<T extends FormikValues = FormikValues>(stages: WizardStage<T>[], values: T) {
    return stages.reduce((mem, stage, i) => (typeof stage.hidden == 'function' && stage.hidden(values) ? [...mem, i] : mem), [] as number[]);
}

function getDefaultSubmitButtonText(submitState: SubmitState, stepsLeft: boolean, t: TFunction) {
    if (stepsLeft) {
        return t<string>('FormSubmitButton.NextStep');
    }
    switch (submitState) {
        case 'Done':
            return t<string>('FormSubmitButton.Done');
        case 'NotSent':
        case 'Error':
            return t<string>('FormSubmitButton.Submit');
        case 'Processing':
            return t<string>('FormSubmitButton.Processing');
        case 'Submitted':
            return t<string>('FormSubmitButton.Submitted');
    }
}

function getNextStep(length: number, hiddenStages: number[], wizardStep: number) {
    let next = wizardStep + 1;
    if (next >= length - hiddenStages.length) {
        return null;
    }
    while (hiddenStages.includes(next)) {
        next = next + 1;
        if (next > length - 1) {
            return null;
        }
    }
    return next;
}

function getStageId(step: number) {
    return `$stage:${step}`;
}

export default Wizard;
